import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import clsx from 'clsx';
import {
  BarChart3,
  Calendar,
  Database,
  FileDown,
  History,
  LayoutDashboard,
  LogOut,
  Menu,
  PlusCircle,
  RotateCcw,
  ScanLine,
  Scale,
  Truck,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../../app/AuthContext';
import { BrandMark } from '../ui/BrandMark';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  roles?: ('ADMIN' | 'OPERATOR' | 'VIEWER')[];
}

interface NavGroup {
  /** `null` = ungruppierter oberster Eintrag (Dashboard), ohne Gruppenlabel. */
  label: string | null;
  items: NavItem[];
}

/**
 * Gruppierte Navigation statt einer flachen Liste - nach Tätigkeitsbereich
 * (BETRIEB/AUSWERTUNG/VERWALTUNG), mit Dashboard als einzigem
 * ungruppiertem Top-Level-Eintrag.
 */
const NAV_GROUPS: NavGroup[] = [
  { label: null, items: [{ to: '/', label: 'Dashboard', icon: LayoutDashboard }] },
  {
    label: 'Betrieb',
    items: [
      { to: '/weighings', label: 'Wiegungen', icon: Scale },
      { to: '/weighings/new', label: 'Erfassen', icon: PlusCircle, roles: ['ADMIN', 'OPERATOR'] },
      { to: '/ocr', label: 'OCR-Erfassung', icon: ScanLine, roles: ['ADMIN', 'OPERATOR'] },
      { to: '/calendar', label: 'Kalender', icon: Calendar },
      { to: '/container-dumps', label: 'Container-Kippung', icon: RotateCcw },
      { to: '/vehicle-log', label: 'Fahrzeug-Log', icon: Truck },
    ],
  },
  {
    label: 'Auswertung',
    items: [
      { to: '/statistics', label: 'Statistik & Archiv', icon: BarChart3 },
      { to: '/reports', label: 'Reports & Export', icon: FileDown },
    ],
  },
];

const ADMIN_GROUP: NavGroup = {
  label: 'Verwaltung',
  items: [
    { to: '/admin/users', label: 'Benutzer', icon: Users },
    { to: '/admin/master-data', label: 'Stammdaten', icon: Database },
    { to: '/admin/events', label: 'Audit-Log', icon: History },
  ],
};

function NavLinkItem({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        clsx(
          'group relative flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
          isActive ? 'bg-accent-500/20 text-white' : 'text-ivory-300 hover:bg-surface-800 hover:text-ivory-100',
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={clsx(
              'absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r bg-accent-400 transition-opacity',
              isActive ? 'opacity-100' : 'opacity-0',
            )}
          />
          <Icon className={clsx('h-4.5 w-4.5 shrink-0', isActive ? 'text-accent-300' : 'text-ivory-500 group-hover:text-ivory-200')} strokeWidth={1.85} />
          {item.label}
        </>
      )}
    </NavLink>
  );
}

function NavGroupList({ groups, isAdmin, onItemClick }: { groups: NavGroup[]; isAdmin: boolean; onItemClick?: () => void }) {
  const allGroups = isAdmin ? [...groups, ADMIN_GROUP] : groups;
  return (
    <nav className="mt-5 flex flex-1 flex-col gap-5" onClick={onItemClick}>
      {allGroups.map((group) => (
        <div key={group.label ?? '_top'} className="flex flex-col gap-0.5">
          {group.label && <div className="mb-1.5 px-3 text-xs font-semibold uppercase tracking-wider text-accent-300/70">{group.label}</div>}
          {group.items.map((item) => (
            <NavLinkItem key={item.to} item={item} />
          ))}
        </div>
      ))}
    </nav>
  );
}

export function AppShell() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = user?.role === 'ADMIN';
  const visibleGroups: NavGroup[] = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((item) => !item.roles || (user && item.roles.includes(user.role))),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="flex min-h-screen bg-surface-950">
      {/* Sidebar - Desktop */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-surface-700 bg-surface-900 p-4 md:flex">
        <Brand />
        <NavGroupList groups={visibleGroups} isAdmin={isAdmin} />
        <UserFooter user={user} onLogout={() => void logout()} />
      </aside>

      {/* Sidebar - Mobile */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <aside className="relative z-10 flex h-full w-64 flex-col bg-surface-900 p-4 shadow-popover">
            <div className="flex items-center justify-between">
              <Brand />
              <button onClick={() => setMobileOpen(false)} aria-label="Menü schließen" className="rounded-md p-1.5 text-ivory-400 hover:bg-surface-800">
                <X className="h-5 w-5" strokeWidth={1.75} />
              </button>
            </div>
            <NavGroupList groups={visibleGroups} isAdmin={isAdmin} onItemClick={() => setMobileOpen(false)} />
            <UserFooter user={user} onLogout={() => void logout()} />
          </aside>
        </div>
      )}

      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-surface-700 bg-surface-950/95 px-4 py-3 md:hidden">
          <Brand compact />
          <button onClick={() => setMobileOpen(true)} aria-label="Menü öffnen" className="rounded-md p-2 text-ivory-300 hover:bg-surface-800">
            <Menu className="h-5 w-5" strokeWidth={1.75} />
          </button>
        </header>

        <main className="relative flex-1 p-4 sm:p-5 md:p-6">
          <div className="mx-auto w-full max-w-7xl">
            <Outlet />
          </div>
        </main>

        <footer className="border-t border-surface-800 px-4 py-4 text-center text-xs text-ivory-500 md:px-6">
          container.lukbra.de · {new Date().getFullYear()}
        </footer>
      </div>
    </div>
  );
}

function UserFooter({ user, onLogout }: { user: { username: string; role: string } | null; onLogout: () => void }) {
  const initials = (user?.username ?? '?').slice(0, 2).toUpperCase();
  return (
    <div className="mt-4 flex items-center gap-2 rounded-md border border-surface-700 bg-surface-850 p-2">
      <Link to="/profile" className="flex min-w-0 flex-1 items-center gap-2.5 rounded px-1 py-0.5 hover:bg-surface-800">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-gradient text-xs font-bold text-white">{initials}</span>
        <span className="min-w-0">
          <p className="truncate text-sm font-medium text-ivory-100">{user?.username}</p>
          <p className="text-xs text-ivory-500">{roleLabel(user?.role)}</p>
        </span>
      </Link>
      <button onClick={onLogout} title="Abmelden" className="rounded-md p-2 text-ivory-400 hover:bg-surface-700 hover:text-ivory-100">
        <LogOut className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className="mb-3 flex items-center gap-2.5 px-1">
      <BrandMark className="h-9 w-9" />
      {!compact && (
        <span className="font-display text-base font-bold tracking-tight text-ivory-100">
          Container<span className="text-accent-400">System</span>
        </span>
      )}
    </div>
  );
}

function roleLabel(role?: string): string {
  if (role === 'ADMIN') return 'Administrator';
  if (role === 'OPERATOR') return 'Erfassung';
  if (role === 'VIEWER') return 'Nur Lesen';
  return '';
}
